// frontend/src/components/SchedulePage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import useApi from '../hooks/useApi';
import { 
    Calendar as CalendarIcon, User, Plus, ChevronLeft, ChevronRight, 
    CheckCircle, Play, Trash2, X, Loader2, Search, FileText, MapPin, Link, Edit 
} from 'lucide-react'; 
import OsNsaFormModal from '../components/modals/OsNsaFormModal';
import NovoRecebimentoModal from '../components/modals/NovoRecebimentoModal';

// --- Helpers de Data ---
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

// NOVO: Helper de formatação de endereço
const formatAddressString = (addr) => {
    if (!addr) return '';
    const parts = [];
    if (addr.address_street) {
        let streetStr = addr.address_street;
        if (addr.address_number) streetStr += `, ${addr.address_number}`;
        parts.push(streetStr);
    }
    if (addr.address_city) {
        let cityStr = addr.address_city;
        if (addr.address_state) cityStr += ` - ${addr.address_state}`;
        parts.push(cityStr);
    }
    return parts.join(' | ');
};

const MONTH_NAMES = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// --- Componente: Busca de Cliente ---
const ClientSearch = ({ onSelect, initialValue = '' }) => {
    const { apiFetch } = useApi();
    const [search, setSearch] = useState(initialValue);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (search && search.length > 2) {
                apiFetch(`/general-clients?search=${search}`)
                    .then(data => {
                        setSuggestions(data || []);
                        setShowSuggestions(true);
                    })
                    .catch(console.error);
            } else {
                setSuggestions([]);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [search, apiFetch]);

    return (
        <div className="relative" ref={wrapperRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
            <div className="relative">
                <input
                    type="text"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Buscar cliente..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={() => search.length > 2 && setShowSuggestions(true)}
                    required
                />
                <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            </div>
            {showSuggestions && suggestions.length > 0 && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {suggestions.map(client => (
                        <li
                            key={client.id}
                            onClick={() => {
                                onSelect(client);
                                setSearch(client.name);
                                setShowSuggestions(false);
                            }}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        >
                            <div className="font-medium">{client.name}</div>
                            <div className="text-xs text-gray-500">{client.document_id}</div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// --- Modal: Novo Agendamento ---
const NewScheduleModal = ({ isOpen, onClose, onSave, preSelectedDate }) => {
    const [clientId, setClientId] = useState('');
    const [selectedClient, setSelectedClient] = useState(null); // NOVO ESTADO
    const [address, setAddress] = useState(''); // NOVO ESTADO
    const [date, setDate] = useState(preSelectedDate || '');
    const [time, setTime] = useState('08:00');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && preSelectedDate) setDate(preSelectedDate);
    }, [isOpen, preSelectedDate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!clientId) return alert("Selecione um cliente.");
        setLoading(true);
        // MODIFICADO: Passando o endereço selecionado para a API
        await onSave({ client_id: clientId, scheduled_date: date, scheduled_time: time, display_address: address });
        setLoading(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-800">Novo Agendamento</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <ClientSearch onSelect={(c) => {
                        setClientId(c.id);
                        setSelectedClient(c);
                        if (c.addresses && c.addresses.length > 0) {
                            setAddress(formatAddressString(c.addresses[0]));
                        } else {
                            setAddress(formatAddressString(c));
                        }
                    }} />
                    
                    {/* NOVO: SELEÇÃO DE ENDEREÇO */}
                    {selectedClient && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Endereço do Atendimento</label>
                            {selectedClient.addresses && selectedClient.addresses.length > 1 ? (
                                <select 
                                    value={address} 
                                    onChange={e => setAddress(e.target.value)} 
                                    className="w-full p-2 border rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    required
                                >
                                    {selectedClient.addresses.map((addr, idx) => (
                                        <option key={idx} value={formatAddressString(addr)}>
                                            {formatAddressString(addr)}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input 
                                    type="text" 
                                    value={address} 
                                    onChange={e => setAddress(e.target.value)} 
                                    className="w-full p-2 border rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 outline-none" 
                                    required 
                                />
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                            <input 
                                type="date" 
                                value={date} 
                                onChange={e => setDate(e.target.value)} 
                                className="w-full p-2 border rounded-md bg-white" 
                                required 
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                            <input 
                                type="time" 
                                value={time} 
                                onChange={e => setTime(e.target.value)} 
                                className="w-full p-2 border rounded-md bg-white" 
                                required 
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancelar</button>
                        <button type="submit" disabled={loading} className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2">
                            {loading && <Loader2 className="animate-spin" size={16} />}
                            Agendar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Modal para Vincular ou Criar OS ---
const LinkOsModal = ({ isOpen, onClose, onCreateNew, onLinkExisting, schedule, apiFetch, formatCurrency }) => {
    const [osId, setOsId] = useState('');
    const [approvedOsList, setApprovedOsList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
        if (isOpen && schedule?.client_id) {
            const fetchApprovedOs = async () => {
                setFetching(true);
                try {
                    const data = await apiFetch(`/os-nsa/client/${schedule.client_id}/approved`);
                    setApprovedOsList(data || []);
                } catch (error) {
                    console.error("Erro ao buscar OS aprovadas do cliente", error);
                } finally {
                    setFetching(false);
                }
            };
            fetchApprovedOs();
        } else {
            setApprovedOsList([]);
            setOsId('');
        }
    }, [isOpen, schedule, apiFetch]);

    if (!isOpen) return null;

    const handleLink = async (e) => {
        e.preventDefault();
        if (!osId) return alert("Selecione uma OS da lista.");
        
        setLoading(true);
        await onLinkExisting(osId);
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 md:p-6 animate-in zoom-in-95 duration-200">
                <div className="text-center mb-6">
                    <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-600">
                        <FileText size={24} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Finalizar Atendimento</h3>
                    <p className="text-sm text-gray-500 mt-1">Já existe uma OS/NSA APROVADA para este cliente?</p>
                </div>

                <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                            <Link size={16} /> Sim, vincular existente
                        </h4>
                        {fetching ? (
                            <div className="flex justify-center p-2"><Loader2 className="animate-spin text-blue-600" /></div>
                        ) : (
                            <form onSubmit={handleLink} className="flex flex-col gap-3">
                                {approvedOsList.length > 0 ? (
                                    <>
                                        <select 
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                            value={osId}
                                            onChange={e => setOsId(e.target.value)}
                                        >
                                            <option value="">-- Selecione a OS Aprovada --</option>
                                            {approvedOsList.map(os => (
                                                <option key={os.id} value={os.id}>
                                                    #{os.os_number} - {new Date(os.data_criacao.replace(' ', 'T')).toLocaleDateString('pt-BR')} - {formatCurrency ? formatCurrency(os.valor_total, 'BRL') : `R$ ${os.valor_total}`}
                                                </option>
                                            ))}
                                        </select>
                                        <button 
                                            type="submit" 
                                            disabled={loading || !osId}
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-semibold disabled:bg-blue-300 w-full flex justify-center items-center"
                                        >
                                            {loading ? <Loader2 className="animate-spin" size={18} /> : 'Vincular'}
                                        </button>
                                    </>
                                ) : (
                                    <p className="text-sm text-gray-500 italic text-center p-2">Nenhuma OS Aprovada encontrada para este cliente.</p>
                                )}
                            </form>
                        )}
                    </div>

                    <div className="relative flex py-2 items-center">
                        <div className="flex-grow border-t border-gray-300"></div>
                        <span className="flex-shrink-0 mx-4 text-gray-400 text-xs uppercase">Ou</span>
                        <div className="flex-grow border-t border-gray-300"></div>
                    </div>

                    <button 
                        onClick={onCreateNew}
                        className="w-full py-3 bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors"
                    >
                        <Plus size={18} />
                        Não, criar nova OS
                    </button>
                </div>

                <div className="mt-6 text-center">
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-sm underline p-2">
                        Cancelar e voltar
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Modal: Detalhes do Atendimento ---
const ScheduleDetailsModal = ({ isOpen, onClose, schedule, onUpdateStatus, onDelete, onOpenOsModal, onEditOs }) => {
    if (!isOpen || !schedule) return null;

    const getStatusColor = (s) => {
        if (s === 'pending') return 'bg-blue-100 text-blue-800 border-blue-200';
        if (s === 'in_progress') return 'bg-green-100 text-green-800 border-green-200';
        if (s === 'finished') return 'bg-gray-100 text-gray-600 border-gray-200';
        return 'bg-gray-100';
    };

    const getStatusLabel = (s) => {
        if (s === 'pending') return 'Agendado';
        if (s === 'in_progress') return 'Em Andamento';
        if (s === 'finished') return 'Finalizado';
        return s;
    };

    const renderOsInfo = () => {
        if (schedule.os_number) {
            return (
                <div className="flex items-center justify-between w-full">
                    <span className="font-mono text-blue-700 font-bold">#{schedule.os_number}</span>
                    <button 
                        onClick={() => onEditOs(schedule)}
                        className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                        title="Editar OS"
                    >
                        <Edit size={16} />
                    </button>
                </div>
            );
        }
        if (schedule.os_nsa_id) {
            return (
                <div className="flex items-center justify-between w-full">
                    <span className="text-gray-500 text-xs">(ID: {schedule.os_nsa_id}) - Número indisponível</span>
                    <button 
                        onClick={() => onEditOs(schedule)}
                        className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                        title="Editar OS"
                    >
                        <Edit size={16} />
                    </button>
                </div>
            );
        }
        return <span className="text-red-500 text-xs font-bold">Não vinculada</span>;
    };

    const openGps = (app) => {
        const address = schedule.display_address;
        if (!address) return;
        
        let url = '';
        if (app === 'google') {
            url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
        } else if (app === 'waze') {
            url = `https://waze.com/ul?q=${encodeURIComponent(address)}`;
        }
        window.open(url, '_blank');
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
                <div className={`px-6 py-4 border-b flex justify-between items-center ${getStatusColor(schedule.status)}`}>
                    <div>
                        <h3 className="text-lg font-bold">{getStatusLabel(schedule.status)}</h3>
                        <p className="text-xs opacity-80">ID: #{schedule.id}</p>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>
                
                <div className="p-6 space-y-4 overflow-y-auto">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-gray-100 rounded-full"><User size={20} className="text-gray-600" /></div>
                        <div>
                            <p className="text-sm font-bold text-gray-700">Cliente</p>
                            <p className="text-base text-gray-900">{schedule.client_name}</p>
                        </div>
                    </div>

                    {schedule.display_address && (
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-gray-100 rounded-full"><MapPin size={20} className="text-gray-600" /></div>
                            <div className="flex-1">
                                <p className="text-sm font-bold text-gray-700">Endereço</p>
                                <p className="text-sm text-gray-600 mb-2">{schedule.display_address}</p>
                                <div className="flex gap-2">
                                    <button 
                                        onClick={() => openGps('google')}
                                        className="px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 flex-1 md:flex-none justify-center flex"
                                    >
                                        Google Maps
                                    </button>
                                    <button 
                                        onClick={() => openGps('waze')}
                                        className="px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 flex-1 md:flex-none justify-center flex"
                                    >
                                        Waze
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-gray-100 rounded-full"><CalendarIcon size={20} className="text-gray-600" /></div>
                        <div>
                            <p className="text-sm font-bold text-gray-700">Agendado para</p>
                            <p className="text-base text-gray-900">
                                {new Date(schedule.scheduled_date + 'T12:00:00').toLocaleDateString('pt-BR')} às {schedule.scheduled_time}
                            </p>
                        </div>
                    </div>

                    {(schedule.start_time || schedule.end_time) && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                            <h4 className="text-sm font-bold text-gray-700 mb-2">Registro de Ponto</h4>
                            <div className="grid grid-cols-1 gap-4">
                                <div className="bg-green-50 p-3 rounded border border-green-100">
                                    <p className="text-xs text-green-600 uppercase font-bold">Início Real</p>
                                    <p className="text-base font-mono text-green-900">
                                        {schedule.start_time ? new Date(schedule.start_time).toLocaleString('pt-BR') : '--/--/---- --:--:--'}
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                                    <p className="text-xs text-gray-600 uppercase font-bold">Fim Real</p>
                                    <p className="text-base font-mono text-gray-900">
                                        {schedule.end_time ? new Date(schedule.end_time).toLocaleString('pt-BR') : '--/--/---- --:--:--'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                      
                    {schedule.status === 'finished' && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                             <div className="flex items-center gap-3 p-3 bg-blue-50 rounded border border-blue-100 text-blue-800">
                                <div className="p-2 bg-white rounded-full shadow-sm shrink-0">
                                    <FileText size={18} className="text-blue-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-bold uppercase text-blue-600 mb-0.5">Ordem de Serviço</p>
                                    <div className="text-sm">
                                        {renderOsInfo()}
                                    </div>
                                </div>
                             </div>
                        </div>
                    )}

                </div>

                <div className="p-4 bg-gray-50 border-t flex flex-col md:flex-row justify-between items-center gap-3 md:gap-0">
                    <button 
                        onClick={() => onDelete(schedule.id)} 
                        className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-md transition-colors w-full md:w-auto flex justify-center"
                        title="Excluir Agendamento"
                    >
                        <Trash2 size={20} /> <span className="md:hidden ml-2 font-semibold">Excluir</span>
                    </button>

                    <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                        {schedule.status === 'pending' && (
                            <button 
                                onClick={() => onUpdateStatus(schedule.id, 'start')}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-semibold shadow-sm w-full md:w-auto"
                            >
                                <Play size={18} /> Iniciar Atendimento
                            </button>
                        )}
                        
                        {schedule.status === 'in_progress' && (
                            <button 
                                onClick={() => onOpenOsModal(schedule)} 
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 font-semibold shadow-sm w-full md:w-auto"
                            >
                                <CheckCircle size={18} /> Finalizar Atendimento
                            </button>
                        )}
                        
                        {schedule.status === 'finished' && (
                            <button
                                onClick={onClose}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-md font-semibold hover:bg-gray-300 w-full md:w-auto"
                            >
                                <CheckCircle size={18} /> Concluído
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Página Principal ---
const SchedulePage = ({ currentUser, formatCurrency, dataChangeKey }) => { 
    const { apiFetch, permissions } = useApi(); 
    const [schedules, setSchedules] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [loading, setLoading] = useState(true);
    
    // --- ESTADOS FINANCEIROS (Necessários para o modal de recebimento) ---
    const [accounts, setAccounts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [accountTypes, setAccountTypes] = useState([]);
    
    const hideSensitiveData = permissions?.comercial_os_hide_sensitive && !currentUser?.is_super_user;

    const [isNewModalOpen, setIsNewModalOpen] = useState(false);
    const [selectedDateForNew, setSelectedDateForNew] = useState('');
    const [selectedSchedule, setSelectedSchedule] = useState(null); 
    
    // Estados para Modais de OS
    const [isOsModalOpen, setIsOsModalOpen] = useState(false);
    const [isLinkOsModalOpen, setIsLinkOsModalOpen] = useState(false);
    const [scheduleToFinish, setScheduleToFinish] = useState(null); 
    const [editingOsFromSchedule, setEditingOsFromSchedule] = useState(null); 

    // Estados para Modal de Faturamento (Recebimento)
    const [isReceivableModalOpen, setIsReceivableModalOpen] = useState(false);
    const [receivableData, setReceivableData] = useState(null);

    // Carrega dados da Agenda
    const fetchSchedules = useCallback(async () => {
        setLoading(true);
        try {
            const month = currentDate.getMonth() + 1;
            const year = currentDate.getFullYear();
            const data = await apiFetch(`/schedule?month=${month}&year=${year}`);
            setSchedules(data || []);
        } catch (error) {
            console.error("Erro ao carregar agenda:", error);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, currentDate]);

    // Carrega dados Financeiros (Contas/Categorias) para o Modal de Recebimento
    useEffect(() => {
        const fetchFinancialData = async () => {
            try {
                const [accData, catData, typesData] = await Promise.all([
                    apiFetch('/accounts'),
                    apiFetch('/categories'),
                    apiFetch('/account-types')
                ]);
                setAccounts(accData || []);
                setCategories(catData || []);
                setAccountTypes(typesData || []);
            } catch (err) {
                console.error("Erro ao carregar dados financeiros na agenda:", err);
            }
        };
        fetchFinancialData();
    }, [apiFetch]);

    useEffect(() => {
        fetchSchedules();
    }, [fetchSchedules]);

    useEffect(() => {
        if (dataChangeKey > 0) {
            fetchSchedules();
        }
    }, [dataChangeKey, fetchSchedules]);

    const handleSaveNew = async (data) => {
        try {
            await apiFetch('/schedule', {
                method: 'POST',
                body: data
            });
            fetchSchedules();
        } catch (error) {
            alert("Erro ao criar agendamento: " + error.message);
        }
    };

    const handleUpdateStatus = async (id, action) => {
        try {
            await apiFetch(`/schedule/${id}/${action}`, { method: 'PUT' });
            fetchSchedules();
            setSelectedSchedule(null); 
        } catch (error) {
            alert("Erro ao atualizar status: " + error.message);
        }
    };

    const handleOpenOsModal = (schedule) => {
        setScheduleToFinish(schedule);
        setIsLinkOsModalOpen(true); 
        setSelectedSchedule(null); 
    };

    const handleEditOs = async (schedule) => {
        if (!schedule.os_nsa_id) return;
        
        try {
            setEditingOsFromSchedule({
                id: schedule.os_nsa_id,
                os_number: schedule.os_number,
                cliente_id: schedule.client_id,
                cliente_nome: schedule.client_name,
                // CORREÇÃO: Repassa o endereço salvo na OS na hora de editar
                cliente_endereco: schedule.display_address
            });
            setSelectedSchedule(null); 
            setIsOsModalOpen(true); 
        } catch (error) {
            console.error("Erro ao preparar edição da OS:", error);
            alert("Erro ao abrir OS para edição.");
        }
    };

    const handleLinkExistingOs = async (osId) => {
        try {
            await handleOsSaved(osId);
            setIsLinkOsModalOpen(false);
            alert("Agendamento finalizado e vinculado à OS com sucesso!");
        } catch (error) {
            alert(error.message || "Erro ao vincular OS.");
        }
    };

    const handleOsSaved = async (osId) => {
        if (!scheduleToFinish && editingOsFromSchedule) {
            setIsOsModalOpen(false);
            setEditingOsFromSchedule(null);
            fetchSchedules();
            return;
        }

        if (!scheduleToFinish || !osId) return;

        try {
            await apiFetch(`/schedule/${scheduleToFinish.id}/finish`, { 
                method: 'PUT',
                body: { osNsaId: osId }
            });
            
            setIsOsModalOpen(false);
            setScheduleToFinish(null);
            
            setTimeout(() => {
                fetchSchedules(); 
            }, 300);
            
        } catch (error) {
            alert("OS salva, mas erro ao finalizar agendamento: " + error.message);
        }
    };

    // --- FUNÇÃO CORRIGIDA: ABRE O MODAL DE NOVO RECEBIMENTO COM DADOS DA OS ---
    const handleFaturarFromSchedule = (osData) => {
        const idOs = editingOsFromSchedule?.id || scheduleToFinish?.os_nsa_id || osData.id;
        
        if(!idOs) {
            alert("Erro: ID da OS não identificado. Salve a OS antes de faturar.");
            return;
        }

        if (!osData.os_number) {
             osData.os_number = editingOsFromSchedule?.os_number || 'PENDENTE';
        }

        const preFilledData = {
            description: `OS #${osData.os_number} - ${osData.cliente_nome}`,
            amount: osData.valor_total || 0,
            due_date: new Date().toISOString().split('T')[0], 
            client_id: osData.cliente_id,
            client_name: osData.cliente_nome, 
            os_nsa_id: idOs,
            status: 'pending'
        };

        setReceivableData(preFilledData);
        setIsReceivableModalOpen(true);
    };

    const handleReceivableSaved = async () => {
        const idOs = receivableData?.os_nsa_id;
        if(idOs) {
             try {
                await apiFetch(`/os-nsa/${idOs}`, {
                    method: 'PUT',
                    body: { faturada: 1, data_faturamento: new Date().toISOString() }
                });
             } catch(e) { console.error("Erro ao atualizar status OS", e); }
        }

        alert('Faturamento realizado com sucesso! Verifique em "Contas a Receber".');
        
        setIsReceivableModalOpen(false);
        setReceivableData(null);
        setIsOsModalOpen(false); 
        setEditingOsFromSchedule(null);
        
        fetchSchedules();
    };

    const handleDelete = async (id) => {
        if (window.confirm("Tem certeza que deseja excluir este agendamento?")) {
            try {
                await apiFetch(`/schedule/${id}`, { method: 'DELETE' });
                fetchSchedules();
                setSelectedSchedule(null);
            } catch (error) {
                alert("Erro ao excluir: " + error.message);
            }
        }
    };

    const daysInMonth = getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());
    const firstDay = getFirstDayOfMonth(currentDate.getFullYear(), currentDate.getMonth());
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const blanks = Array.from({ length: firstDay }, (_, i) => i);

    const changeMonth = (offset) => {
        const newDate = new Date(currentDate.setMonth(currentDate.getMonth() + offset));
        setCurrentDate(new Date(newDate));
    };

    const getEventsForDay = (day) => {
        const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return schedules.filter(s => s.scheduled_date === dateStr);
    };

    return (
        <div className="p-3 md:p-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <CalendarIcon className="text-blue-600" /> Agenda de Serviços
                    </h1>
                    <p className="text-sm md:text-base text-gray-500">Gerencie os atendimentos técnicos e acompanhe o progresso.</p>
                </div>
                <button 
                    onClick={() => { setSelectedDateForNew(''); setIsNewModalOpen(true); }}
                    className="mt-4 md:mt-0 w-full md:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-semibold flex items-center justify-center gap-2 shadow-sm"
                >
                    <Plus size={20} /> Novo Agendamento
                </button>
            </div>

            <div className="bg-white p-4 rounded-t-lg shadow-sm border border-gray-200 border-b-0 flex items-center justify-between">
                <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-gray-100 rounded-full"><ChevronLeft /></button>
                <h2 className="text-lg md:text-xl font-bold text-gray-800 capitalize">
                    {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <button onClick={() => changeMonth(1)} className="p-2 hover:bg-gray-100 rounded-full"><ChevronRight /></button>
            </div>

            <div className="bg-white rounded-b-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
                    {WEEKDAYS.map(day => (
                        <div key={day} className="py-3 text-center text-xs md:text-sm font-semibold text-gray-500 uppercase">
                            {day}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7 auto-rows-fr bg-gray-200 gap-px border-gray-200">
                    {blanks.map((_, i) => (
                        <div key={`blank-${i}`} className="bg-gray-50 min-h-[80px] md:min-h-[120px]"></div>
                    ))}

                    {days.map(day => {
                        const events = getEventsForDay(day);
                        const isToday = 
                            day === new Date().getDate() && 
                            currentDate.getMonth() === new Date().getMonth() && 
                            currentDate.getFullYear() === new Date().getFullYear();

                        return (
                            <div 
                                key={day} 
                                className={`bg-white min-h-[80px] md:min-h-[120px] p-1 md:p-2 transition-colors hover:bg-blue-50/30 group relative border-b border-r border-gray-100 ${isToday ? 'bg-blue-50' : ''}`}
                                onClick={() => {
                                    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                    setSelectedDateForNew(dateStr);
                                    setIsNewModalOpen(true);
                                }}
                            >
                                <div className="flex justify-between items-start">
                                    <span className={`text-xs md:text-sm font-semibold w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-full ${isToday ? 'bg-blue-600 text-white' : 'text-gray-700'}`}>
                                        {day}
                                    </span>
                                    <button className="hidden md:block opacity-0 group-hover:opacity-100 text-blue-600 hover:bg-blue-100 p-1 rounded-full transition-opacity">
                                        <Plus size={14} />
                                    </button>
                                </div>
                                
                                <div className="mt-1 md:mt-2 space-y-1">
                                    {events.map(ev => (
                                        <div 
                                            key={ev.id}
                                            onClick={(e) => {
                                                e.stopPropagation(); 
                                                setSelectedSchedule(ev);
                                            }}
                                            className={`text-[10px] md:text-xs p-1 md:p-1.5 rounded cursor-pointer border-l-2 truncate shadow-sm transition-transform hover:scale-[1.02] ${
                                                ev.status === 'in_progress' 
                                                    ? 'bg-green-50 border-green-500 text-green-800' 
                                                    : ev.status === 'finished'
                                                        ? 'bg-gray-100 border-gray-400 text-gray-500 line-through opacity-70'
                                                        : 'bg-blue-50 border-blue-500 text-blue-700'
                                            }`}
                                        >
                                            <span className="font-bold mr-1">{ev.scheduled_time}</span>
                                            {ev.client_name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            
            <NewScheduleModal 
                isOpen={isNewModalOpen} 
                onClose={() => setIsNewModalOpen(false)} 
                onSave={handleSaveNew}
                preSelectedDate={selectedDateForNew}
            />

            <ScheduleDetailsModal 
                isOpen={!!selectedSchedule} 
                onClose={() => setSelectedSchedule(null)} 
                schedule={selectedSchedule}
                onUpdateStatus={handleUpdateStatus}
                onDelete={handleDelete}
                onOpenOsModal={handleOpenOsModal}
                onEditOs={handleEditOs} 
            />

            <LinkOsModal
                isOpen={isLinkOsModalOpen}
                onClose={() => {
                    setIsLinkOsModalOpen(false);
                    setScheduleToFinish(null);
                }}
                onCreateNew={() => {
                    setIsLinkOsModalOpen(false);
                    setIsOsModalOpen(true); 
                }}
                onLinkExisting={handleLinkExistingOs}
                schedule={scheduleToFinish}
                apiFetch={apiFetch}
                formatCurrency={formatCurrency}
            />

            {/* Modal de OS */}
            {isOsModalOpen && (scheduleToFinish || editingOsFromSchedule) && (
                <OsNsaFormModal
                    osToEdit={editingOsFromSchedule || { 
                        cliente_id: scheduleToFinish?.client_id,
                        cliente_nome: scheduleToFinish?.client_name,
                        // CORREÇÃO: Passa o endereço selecionado no agendamento para a Nova OS
                        cliente_endereco: scheduleToFinish?.display_address 
                    }}
                    onClose={() => {
                        setIsOsModalOpen(false);
                        setScheduleToFinish(null);
                        setEditingOsFromSchedule(null);
                    }}
                    onSave={handleOsSaved} 
                    onFaturar={handleFaturarFromSchedule}
                    currentUser={currentUser}
                    formatCurrency={formatCurrency}
                    hideSensitiveData={hideSensitiveData} 
                    initialStatus="Aprovada"
                />
            )}

            {/* Modal de Novo Recebimento (Faturamento) */}
            {isReceivableModalOpen && (
                <div style={{ position: 'relative', zIndex: 9999 }}>
                    <NovoRecebimentoModal
                        isOpen={isReceivableModalOpen}
                        onClose={() => setIsReceivableModalOpen(false)}
                        onSave={handleReceivableSaved}
                        initialData={receivableData}
                        accounts={accounts}
                        categories={categories}
                        accountTypes={accountTypes}
                        isEdit={false}
                    />
                </div>
            )}
        </div>
    );
};

export default SchedulePage;